import React, { useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";

const emptyForm = {
  concession_date: new Date().toISOString().slice(0, 10),
  fee_amount: "",
  remarks: "",
};

const sumAmounts = (items, approvedOnly = false) =>
  (items || [])
    .filter(
      (item) =>
        !approvedOnly ||
        !item.workflow_status ||
        String(item.workflow_status).toUpperCase() === "APPROVED"
    )
    .reduce((sum, item) => sum + Number(item.fee_amount || 0), 0);

const formatAmount = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const mapFeeBook = (row) => {
  const feeBookAmount = Number(row.fee_amount || 0);
  const receipts = sumAmounts(row.feeReceipts);
  const concessions = sumAmounts(row.feeConcessions, true);
  const refunds = sumAmounts(row.feeRefunds, true);
  return {
    ...row,
    id: String(row.fee_book_id),
    fee_head_name: row.feeHead?.fee_head_name || "",
    fee_book_amount: feeBookAmount,
    receipt_amount: receipts,
    concession_amount: concessions,
    refund_amount: refunds,
    balance_amount: Math.max(feeBookAmount - receipts - concessions + refunds, 0),
  };
};

export default function FeeConcession() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const userEmail = user?.user_email || "";
  const rollNoInputRef = useRef(null);
  const [rollNo, setRollNo] = useState("");
  const [student, setStudent] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFeeBook, setSelectedFeeBook] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [proofFile, setProofFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const showMessage = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const searchStudent = async () => {
    const value = rollNo.trim();
    if (!value) return showMessage("Enter Student Roll No.", "warning");
    setLoading(true);
    try {
      const studentResponse = await axios.get(
        `${API_BASE_URL}/api/students/rollno/${clientId}/${collegeId}/${encodeURIComponent(value)}`
      );
      const foundStudent = studentResponse.data?.student || studentResponse.data?.data || null;
      if (!foundStudent?.student_id) throw new Error("Student Details are not found");
      setStudent(foundStudent);

      const feeBookResponse = await axios.get(
        `${API_BASE_URL}/api/fee-books/${clientId}/${collegeId}/student/${foundStudent.student_id}`,
        { params: { workflowStatus: "ALL" } }
      );
      const feeBooks = Array.isArray(feeBookResponse.data?.data)
        ? feeBookResponse.data.data.map(mapFeeBook)
        : [];
      setRows(feeBooks);
      if (!feeBooks.length) showMessage("No Fee Books found for this student", "info");
    } catch (error) {
      setStudent(null);
      setRows([]);
      showMessage(
        error.response?.data?.message || error.message || "Unable to find student",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const startConcession = () => {
    if (rollNo.trim()) {
      searchStudent();
      return;
    }
    rollNoInputRef.current?.focus();
    showMessage("Enter Student Roll No. to add a concession", "info");
  };

  const openConcession = (row) => {
    if (row.balance_amount <= 0)
      return showMessage("This Fee Book has no remaining balance", "warning");
    setSelectedFeeBook(row);
    setForm(emptyForm);
    setProofFile(null);
  };

  const saveConcession = async () => {
    const amount = Number(form.fee_amount);
    if (!amount || amount <= 0) return showMessage("Enter a valid concession amount", "warning");
    if (amount > selectedFeeBook.balance_amount)
      return showMessage("Concession amount cannot exceed remaining balance", "warning");
    if (!form.remarks.trim()) return showMessage("Enter concession remarks", "warning");
    if (!proofFile) return showMessage("Upload the concession proof image", "warning");

    setSaving(true);
    try {
      const proofData = new FormData();
      proofData.append("proof", proofFile);
      const proofResponse = await axios.post(
        `${API_BASE_URL}/api/fee-concessions/${clientId}/${collegeId}/proof`,
        proofData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const { data } = await axios.post(
        `${API_BASE_URL}/api/fee-concessions/${clientId}/${collegeId}`,
        {
          fee_book_id: selectedFeeBook.fee_book_id,
          student_id: student.student_id,
          student_rollno: student.student_rollno,
          concession_date: form.concession_date,
          fee_amount: amount,
          concession_proof: proofResponse.data?.data?.path || "",
          remarks: form.remarks.trim(),
          workflow_status: "DRAFT",
          created_by: userEmail,
        }
      );
      showMessage(data?.message || "Fee Concession saved as Draft");
      setSelectedFeeBook(null);
      setProofFile(null);
      await searchStudent();
    } catch (error) {
      showMessage(error.response?.data?.message || "Unable to save Fee Concession", "error");
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        width: 70,
        sortable: false,
        valueGetter: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
      },
      { field: "fee_head_name", headerName: "Fee Book / Fee Head", minWidth: 220, flex: 1 },
      {
        field: "workflow_status",
        headerName: "Status",
        width: 110,
      },
      {
        field: "fee_book_amount",
        headerName: "Total Amount",
        width: 130,
        valueFormatter: ({ value }) => formatAmount(value),
      },
      {
        field: "receipt_amount",
        headerName: "Old Paid",
        width: 115,
        valueFormatter: ({ value }) => formatAmount(value),
      },
      {
        field: "concession_amount",
        headerName: "Previous Concession",
        width: 170,
        valueFormatter: ({ value }) => formatAmount(value),
      },
      {
        field: "balance_amount",
        headerName: "Present Balance",
        width: 145,
        valueFormatter: ({ value }) => formatAmount(value),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 170,
        sortable: false,
        renderCell: ({ row }) => {
          const canAdd =
            String(row.workflow_status || "").toUpperCase() === "APPROVED" &&
            row.balance_amount > 0;
          return (
            <Button
              size="medium"
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!canAdd}
              onClick={() => openConcession(row)}
              sx={{ color: "#fff" }}
            >
              Add Concession
            </Button>
          );
        },
      },
    ],
    []
  );

  return (
    <Box p={2} pb={6}>
      <Paper elevation={1}>
        <Box px={3} pt={2}>
          <Typography variant="h6">FEE CONCESSION</Typography>
        </Box>
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          <TextField
            inputRef={rollNoInputRef}
            size="medium"
            label="Student Roll No."
            value={rollNo}
            onChange={(event) => setRollNo(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && searchStudent()}
            sx={{ minWidth: { xs: "100%", sm: 380 } }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={searchStudent}
            disabled={loading}
            sx={{ color: "#fff" }}
          >
            Search
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={startConcession}
            disabled={loading}
            sx={{ color: "#fff", whiteSpace: "nowrap" }}
          >
            Add Fee Concession
          </Button>
        </Toolbar>

        {student && (
          <Box px={3} pb={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" mb={1.5}>
                Student Details
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                }}
              >
                {[
                  ["Student Name", student.student_name],
                  ["Roll Number", student.student_rollno],
                  ["Mobile", student.mobile],
                  ["Status", student.student_status],
                ].map(([label, value]) => (
                  <Box key={label}>
                    <Typography variant="caption" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {value || "-"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>
        )}

        {student && (
          <Box sx={{ width: "100%" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              pagination
              page={page}
              pageSize={pageSize}
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(newPageSize) => {
                setPageSize(newPageSize);
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              disableSelectionOnClick
            />
          </Box>
        )}
      </Paper>

      <Dialog
        open={Boolean(selectedFeeBook)}
        onClose={() => setSelectedFeeBook(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Fee Concession</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography fontWeight="bold" mb={1.5}>
                {selectedFeeBook?.fee_head_name || "Fee Book"}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                  gap: 1.25,
                }}
              >
                <Typography>
                  Total Amount: ₹{formatAmount(selectedFeeBook?.fee_book_amount)}
                </Typography>
                <Typography>Old Paid: ₹{formatAmount(selectedFeeBook?.receipt_amount)}</Typography>
                <Typography>
                  Previous Concession: ₹{formatAmount(selectedFeeBook?.concession_amount)}
                </Typography>
                <Typography>Refunds: ₹{formatAmount(selectedFeeBook?.refund_amount)}</Typography>
              </Box>
              <Typography variant="h6" color="primary" mt={1.5}>
                Present Balance: ₹{formatAmount(selectedFeeBook?.balance_amount)}
              </Typography>
            </Paper>
            <TextField
              required
              size="medium"
              type="number"
              label="Concession Amount"
              value={form.fee_amount}
              inputProps={{ min: 0.01, max: selectedFeeBook?.balance_amount || 0, step: 0.01 }}
              onChange={(event) => setForm((old) => ({ ...old, fee_amount: event.target.value }))}
            />
            <TextField
              size="medium"
              type="date"
              label="Concession Date"
              value={form.concession_date}
              InputLabelProps={{ shrink: true }}
              onChange={(event) =>
                setForm((old) => ({ ...old, concession_date: event.target.value }))
              }
            />
            <Box>
              <Typography
                component="label"
                variant="body2"
                fontWeight={600}
                color="text.primary"
                display="block"
                mb={0.75}
              >
                Concession Proof{" "}
                <Box component="span" color="error.main">
                  *
                </Box>
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                component="label"
                startIcon={<UploadFileIcon />}
                sx={{
                  color: "#1976d2",
                  borderColor: "#1976d2",
                  backgroundColor: "#fff",
                  "&:hover": {
                    color: "#115293",
                    borderColor: "#115293",
                    backgroundColor: "#f5f9ff",
                  },
                }}
              >
                {proofFile ? "Change Concession Proof" : "Upload Concession Proof"}
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(event) => setProofFile(event.target.files?.[0] || null)}
                />
              </Button>
            </Box>
            {proofFile && (
              <Box>
                <Typography variant="body2" fontWeight="bold" color="success.main" mb={1}>
                  Proof Uploaded: {proofFile.name}
                </Typography>
                <Box
                  component="img"
                  src={URL.createObjectURL(proofFile)}
                  alt="Concession proof preview"
                  sx={{ maxWidth: "100%", maxHeight: 220, borderRadius: 1 }}
                />
              </Box>
            )}
            <TextField
              required
              size="medium"
              label="Remarks"
              multiline
              minRows={2}
              value={form.remarks}
              onChange={(event) => setForm((old) => ({ ...old, remarks: event.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setSelectedFeeBook(null)}
            sx={{ backgroundColor: "#d32f2f", color: "#fff" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={saveConcession}
            sx={{ color: "#fff" }}
          >
            Save as Draft
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((old) => ({ ...old, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((old) => ({ ...old, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
